import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import styles from "./Services.module.css";
import { FaChevronRight, FaChevronLeft } from "react-icons/fa";
import { Link } from "react-router-dom";
import serviceData from "data/carouselData.json";
import { mapIcons } from "utils";

const mappedServiceData = serviceData.map(mapIcons);
const Services = () => {
  // Create a properly duplicated array for infinite looping
  // Duplicate the entire array once for smoother looping
  // Create sufficient duplicates to ensure smooth continuous scrolling
  const extendedServices = useMemo(
    () => [...mappedServiceData, ...mappedServiceData, ...mappedServiceData],
    [mappedServiceData]
  );

  const [activeIndex, setActiveIndex] = useState(0);
  const [autoScroll, setAutoScroll] = useState(true);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [position, setPosition] = useState(0);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [cardWidth, setCardWidth] = useState(0);
  const [gap, setGap] = useState(30);
  const carouselRef = useRef(null);
  const trackRef = useRef(null);
  const cardRefs = useRef([]);
  const autoScrollRef = useRef(null);
  const lastTimeRef = useRef(0);

  // Initialize card refs
  cardRefs.current = extendedServices.map(
    (_, i) => cardRefs.current[i] || React.createRef()
  );

  // Measure card width and gap on mount and resize
  const measureCarousel = useCallback(() => {
    if (cardRefs.current[0]?.current && trackRef.current) {
      const cardEl = cardRefs.current[0].current;
      const computed = window.getComputedStyle(trackRef.current);
      const computedGap = parseInt(computed.gap) || 30;

      // Set measured dimensions
      setCardWidth(cardEl.offsetWidth);
      setGap(computedGap);

      // Update mobile state
      setIsMobile(window.innerWidth <= 768);
    }
  }, []);

  // The core continuous scrolling animation function
  const animateScroll = useCallback(
    (timestamp) => {
      if (!autoScroll || !trackRef.current) {
        autoScrollRef.current = requestAnimationFrame(animateScroll);
        return;
      }

      if (!lastTimeRef.current) {
        lastTimeRef.current = timestamp;
      }

      const elapsed = timestamp - lastTimeRef.current;

      if (elapsed > 17) {
        // Roughly 60fps
        // Calculate new position - slow and steady movement
        const pixelsPerSecond = isMobile ? 30 : 40;
        const delta = (pixelsPerSecond * elapsed) / 1000;

        // Update position
        const itemWidth = cardWidth + gap;
        const totalSetWidth = mappedServiceData.length * itemWidth;
        let newPosition = position - delta;

        // Reset when we've scrolled past the first set of items
        // if (Math.abs(newPosition) >= services.length * itemWidth) {
        //   newPosition = 0;
        //   setTransitionEnabled(false);
        // }

        setPosition(newPosition);
        lastTimeRef.current = timestamp;

        // Calculate active index based on position
        if (-newPosition >= totalSetWidth) {
          newPosition = newPosition + totalSetWidth;
          setTransitionEnabled(false);
        }

        setPosition(newPosition);
        lastTimeRef.current = timestamp;

        // Calculate active index based on position
        // We use modulo to get the correct index within the original services array
        const newIndex =
          Math.floor(Math.abs(newPosition) / itemWidth) %
          mappedServiceData.length;
        if (newIndex !== activeIndex) {
          setActiveIndex(newIndex);
        }
      }

      autoScrollRef.current = requestAnimationFrame(animateScroll);
    },
    [
      autoScroll,
      position,
      cardWidth,
      gap,
      isMobile,
      mappedServiceData.length,
      activeIndex,
    ]
  );

  // Initialize and clean up animation frame
  useEffect(() => {
    autoScrollRef.current = requestAnimationFrame(animateScroll);

    return () => {
      if (autoScrollRef.current) {
        cancelAnimationFrame(autoScrollRef.current);
      }
    };
  }, [animateScroll]);

  // Initialize measurements on mount and handle resize
  useEffect(() => {
    measureCarousel();

    // Handle window resize
    const handleResize = () => {
      measureCarousel();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [measureCarousel]);

  // Re-enable transition after position reset
  useEffect(() => {
    if (!transitionEnabled) {
      const timer = setTimeout(() => {
        setTransitionEnabled(true);
      }, 50);
      return () => clearTimeout(timer);
    }
  }, [transitionEnabled]);

  // Navigate to specific slide
  const goToSlide = useCallback(
    (index) => {
      const targetPosition = -(index * (cardWidth + gap));
      setPosition(targetPosition);
      setActiveIndex(index % mappedServiceData.length);
      setAutoScroll(false);
    },
    [cardWidth, gap, mappedServiceData.length]
  );

  // Navigate to previous slide
  const goToPrevSlide = useCallback(() => {
    // Get real index in full array
    const realIndex =
      activeIndex === 0
        ? mappedServiceData.length - 1 // Wrap to end
        : activeIndex - 1;

    goToSlide(realIndex);
  }, [activeIndex, goToSlide, mappedServiceData.length]);

  // Navigate to next slide
  const goToNextSlide = useCallback(() => {
    // Get real index in full array
    const realIndex =
      activeIndex === mappedServiceData.length - 1
        ? 0 // Wrap to beginning
        : activeIndex + 1;

    goToSlide(realIndex);
  }, [activeIndex, goToSlide, mappedServiceData.length]);

  // Handle card hover
  const handleCardHover = useCallback((index, isHovering) => {
    if (isHovering) {
      setHoveredIndex(index);
      setAutoScroll(false);
    } else {
      setHoveredIndex(null);
      // Only resume if not hovering on the section
      if (!carouselRef.current || !carouselRef.current.matches(":hover")) {
        setAutoScroll(true);
      }
    }
  }, []);

  // Section mouse enter/leave handlers
  const handleSectionMouseEnter = useCallback(() => {
    setAutoScroll(false);
  }, []);

  const handleSectionMouseLeave = useCallback(() => {
    if (hoveredIndex === null) {
      setAutoScroll(true);
    }
  }, [hoveredIndex]);

  // Calculate visible index for active styling
  const getCardStyle = useCallback(
    (index) => {
      // Only apply active style on hover
      const isActive = hoveredIndex === index;

      // Match card with service title for special styling
      const cardService = extendedServices[index];
      const isProviderCredentialing =
        cardService.title === "Provider Credentialing";

      return {
        className: `${styles.serviceCardWrapper} ${
          isActive ? styles.active : ""
        } ${isProviderCredentialing ? styles.providerCredentialing : ""}`,
      };
    },
    [extendedServices, hoveredIndex]
  );

  return (
    <section className={styles.servicesSection}>
      <div className={styles.servicesContainer}>
        <header>
          <p className={styles.subtitle}>Our Services</p>
          <h2 className={styles.sectionTitle}>What We Are Best At</h2>
          <p className={styles.servicesIntro}>
            {/* Experience white-glove credentialing and compliance tailored to your needs—use our software with customized templates for in-house management or fully outsource the process to our experts for a hassle-free solution */}
            We know every healthcare organization is unique, so we provide
            tailored white-glove services to meet your specific needs.
          </p>
        </header>

        <div
          className={styles.carouselContainer}
          role="region"
          aria-label="Services carousel"
        >
          {/* Left Arrow */}
          <button
            className={`${styles.carouselArrow} ${styles.arrowLeft}`}
            onClick={(e) => {
              e.stopPropagation();
              goToPrevSlide();
            }}
            aria-label="Previous slide"
          >
            <FaChevronLeft aria-hidden="true" />
          </button>

          {/* Right Arrow */}
          <button
            className={`${styles.carouselArrow} ${styles.arrowRight}`}
            onClick={(e) => {
              e.stopPropagation();
              goToNextSlide();
            }}
            aria-label="Next slide"
          >
            <FaChevronRight aria-hidden="true" />
          </button>

          <div
            className={styles.servicesCarousel}
            ref={carouselRef}
            onMouseEnter={handleSectionMouseEnter}
            onMouseLeave={handleSectionMouseLeave}
          >
            <div
              className={styles.serviceTrack}
              ref={trackRef}
              style={{
                transform: `translateX(${position}px)`,
                transition: transitionEnabled ? "transform 1.0s ease" : "none",
              }}
            >
              {extendedServices.map((service, index) => {
                const cardProps = getCardStyle(index);

                return (
                  <article
                    {...cardProps}
                    key={`${service.title}-${index}`}
                    ref={cardRefs.current[index]}
                    onClick={() => goToSlide(index)}
                    onMouseEnter={() => handleCardHover(index, true)}
                    onMouseLeave={() => handleCardHover(index, false)}
                  >
                    <div className={styles.serviceCard}>
                      <header className={styles.serviceCardHeader}>
                        <figure className={styles.serviceIcon}>
                          {/* <ServiceIcon type={service.icon} /> */}
                          <img
                            src={service.icon}
                            className={styles.subHeaderIcon}
                            alt={service.title}
                          />
                        </figure>
                        <h3>{service.title}</h3>
                      </header>

                      <div className={styles.serviceCardBody}>
                        <div className={styles.serviceContent}>
                          <p className={styles.serviceDescription}>
                            {service.description}
                          </p>

                          <hr className={styles.serviceDivider} />

                          <ul className={styles.serviceFeatures}>
                            <div className={styles.featureWrapper}>
                              {service.features.map((feature, i) => (
                                <li key={i}>
                                  <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={styles.checkCircle}
                                    aria-hidden="true"
                                  >
                                    <circle
                                      cx="10"
                                      cy="10"
                                      r="9"
                                      stroke="#4A7C59"
                                      strokeWidth="2"
                                    />
                                    <path
                                      d="M6 10L9 13L14 7"
                                      stroke="#4A7C59"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                  <span className={styles.featureText}>
                                    {feature}
                                  </span>
                                </li>
                              ))}
                            </div>
                          </ul>
                        </div>

                        <footer className={styles.serviceFooterContainer}>
                          <hr className={styles.serviceDivider} />

                          <nav className={styles.serviceFooter}>
                            {/* <Link to={service.path} className={styles.readMore}> */}
                            <Link to={service.path} className={styles.readMore}>
                              Read More About {service.title}
                              <span
                                className={styles.doubleArrow}
                                aria-hidden="true"
                              >
                                <FaChevronRight />
                                <FaChevronRight
                                  className={styles.secondArrow}
                                />
                              </span>
                            </Link>
                          </nav>
                        </footer>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          <nav
            className={styles.carouselControls}
            aria-label="Carousel navigation"
          >
            <div className={styles.carouselDots} role="tablist">
              {mappedServiceData.map((_, index) => (
                <button
                  key={index}
                  className={`${styles.carouselDot} ${
                    activeIndex === index ? styles.active : ""
                  }`}
                  onClick={() => {
                    goToSlide(index);
                  }}
                  role="tab"
                  aria-selected={activeIndex === index}
                  aria-label={`Go to slide ${index + 1}`}
                ></button>
              ))}
            </div>
          </nav>
        </div>
      </div>
    </section>
  );
};

export default Services;
